import React, { useContext } from 'react';

import cx from 'classnames';
import { always, map, pipe, propSatisfies, reject, unless } from 'ramda';
import { useSelector } from 'react-redux';

import MobileNav from 'components/Layouts/Form/FormBuilder/MobileNav';
import Icon from 'components/shared/Icon';
import SAFARI_PORTAL_LOGO from 'components/shared/images/safari_portal.svg';
import LandingPageContext from 'components/shared/LandingPage/contexts/LandingPageContext';
import { MOB_APP_BUTTON_MOUNT_POINT } from 'components/shared/MobileAppInfo/constants';
import Tooltip from 'components/shared/Tooltip';
import Text from 'components/shared/Typography/Text';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { isAuthenticatedSelector } from 'store/auth/selectors';
import { isMobileBrowser } from 'utils/browserDetect';
import { LANDING_THEMES } from 'utils/itineraries/functions';
import { isNotDefined } from 'utils/ramda';

import { iconsConfig } from '../constants';
import styles from '../index.module.css';
import { BurgerMenu } from '../InlineBuilderItinerary';
import MoreActionsMenu from '../MoreActionsMenu';
import NotificationCenter from '../NotificationCenter';
import PageHeaderMenu from '../PageHeaderMenu';
import { IIcon, IIconConfig } from '../types';
import { withNotificationCenter } from '../utils';
import { Wrapper } from '../Wrapper';
import { IRenderMenuParams, IPortalItineraryProps } from './types';

const renderMenuDefault = ({
  isTabletOrMobile,
  isAuthenticated,
  hasUserMenu,
  userMenuActions,
  isDefaultTheme,
  notifications,
}: IRenderMenuParams) => {
  const renderNotificationCenter = () => {
    if (isNotDefined(notifications)) return null;

    return <NotificationCenter notifications={notifications} />;
  };

  return (
    !isTabletOrMobile && (
      <>
        {!isDefaultTheme && renderNotificationCenter()}
        {isAuthenticated && <PageHeaderMenu actions={hasUserMenu ? userMenuActions : []} />}
      </>
    )
  );
};

const PortalItinerary = ({
  links,
  disabled = false,
  renderMenu = renderMenuDefault,
  renderIcons: propRenderIcons,
}: IPortalItineraryProps) => {
  const { headerOptions, landingTheme } = useContext(LandingPageContext);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { isTabletOrMobile } = useMediaBreakpoints();
  const isMobileDevice = isMobileBrowser();
  const isDefaultTheme = landingTheme === LANDING_THEMES.default;
  const isBoatsTheme = landingTheme === LANDING_THEMES.boats;
  const isDefaultLayout = isDefaultTheme || isBoatsTheme;
  const {
    logoSrc,
    logoCustomStyle,
    icons,
    companyName,
    userMenuActions,
    hasUserMenu,
    notifications = [],
    iconsParams = {},
  } = headerOptions;

  const renderLogo = () => {
    if (!logoSrc && companyName) {
      return (
        <Text hoefler italic className={cx(styles.companyName, 'custom-right-header-companyname')}>
          {companyName}
        </Text>
      );
    }

    return (
      <img
        src={logoSrc || SAFARI_PORTAL_LOGO}
        alt="logo"
        style={logoCustomStyle}
        className="custom-right-header-logo"
        crossOrigin=""
      />
    );
  };

  const renderIcon = (icon: IIcon) => {
    const iconConfig: IIconConfig = iconsConfig[icon.type];

    if ((!isDefaultTheme || isNotDefined(notifications)) && icon.type === 'help') {
      return null;
    }

    if (iconConfig && (!isTabletOrMobile || icon.type === 'phone')) {
      const containerClasses = cx(
        styles.iconWrapper,
        iconsParams.margin === 'mini' && styles.iconWrapperMarginMini,
        'custom-right-header-icon-wrapper',
        { [styles.customIconWrapper]: !isDefaultTheme },
      );

      const iconClasses = cx(
        disabled && styles.notAllowedCursor,
        styles.icon,
        iconConfig.className,
        iconConfig.classNamePortal,
        'custom-right-header-icon',
        `custom-right-header-icon-${icon.type}`,
      );

      const IconComponent = (
        <Icon
          component={iconConfig.icon}
          className={iconClasses}
          data-tip
          data-for={`right_header_icon_${icon.type}`}
        />
      );

      return (
        <div key={icon.type} className={containerClasses} onClick={unless(always(disabled), icon.onClick)}>
          {iconConfig.customElement ? withNotificationCenter(IconComponent, notifications) : IconComponent}
          {disabled ? (
            // @ts-ignore
            <Tooltip id={`right_header_icon_${icon.type}`}>Active on live itinerary link when shared.</Tooltip>
          ) : (
            // @ts-ignore
            <Tooltip id={`right_header_icon_${icon.type}`}>{iconConfig.getHint()}</Tooltip>
          )}
        </div>
      );
    }

    return null;
  };

  const renderIcons = propRenderIcons || pipe(reject(propSatisfies(Boolean, 'hidden')), map(renderIcon));

  return (
    <Wrapper links={links}>
      <div className={cx(styles.headerPortal, styles.headerPortalItinerary, 'custom-right-header')} id="landingHeader">
        {isDefaultLayout && (
          <BurgerMenu
            links={links}
            classname={cx(styles.navigation, isBoatsTheme && styles['navigation-right'])}
            disabled={disabled}
          />
        )}
        <div className={cx(styles.baseHeaderWrapper, !isDefaultLayout && styles.baseCustomHeaderWrapper)}>
          {isTabletOrMobile && !isDefaultLayout && (
            <div className={cx(styles.iconsContainer)}>
              {isMobileDevice && <div id={MOB_APP_BUTTON_MOUNT_POINT} />}
              <div className={cx(styles.headerActions, 'custom-right-header-actions')}>{renderIcons(icons)}</div>
            </div>
          )}
          <div className="custom-right-header-user">
            <div className={!isTabletOrMobile && (styles.renderMenu as any)}>
              {renderMenu({
                isTabletOrMobile,
                isAuthenticated,
                hasUserMenu,
                userMenuActions,
                isDefaultTheme,
                notifications,
              })}
            </div>
          </div>

          <div
            className={cx(isDefaultLayout ? styles.headerLogo : styles.customHeaderLogo, 'custom-right-header-logo')}
          >
            {renderLogo()}
          </div>
          {isMobileDevice ? (
            isDefaultLayout && (
              <div className={cx(styles.mobileRightActions, 'custom-right-header-actions-wrapper')}>
                <div id={MOB_APP_BUTTON_MOUNT_POINT} />
                <MoreActionsMenu actions={userMenuActions} icons={icons} notifications={notifications} />
              </div>
            )
          ) : (
            <div className={cx(styles.headerActions, disabled && styles.unlockIcons, 'custom-right-header-actions')}>
              {renderIcons(icons)}
            </div>
          )}
        </div>
      </div>
      {/*@ts-ignore*/}
      {!isTabletOrMobile && !isDefaultLayout && <MobileNav links={links} navigation id="mobileNav" unlock={disabled} />}
    </Wrapper>
  );
};

export default PortalItinerary;
